import { Mail, Search, User, Xmark } from 'iconoir-react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { DataTableFacetedFilter } from 'components/blocks/data-table/data-table-faceted-filter';
import { DateRangeFilter } from 'components/blocks/data-table/data-table-date-filter';
import { statuses } from 'features/iam/components/iam-table/iam-table-filter-data';
import type { TeamsPeopleSearchMode } from '../map-iam-user-to-teams-member';
import {
  TeamsPeopleFilter,
  type TeamsPeopleFilters,
  type TeamsPlanFilterOption,
} from './teams-people-filter';
import {
  TeamsViewColumnsPopover,
  type TeamsPeopleColumnVisibility,
} from './teams-view-columns-popover';
import { teamsOutlineCardButtonClassName } from './teams-styles';

type TeamsPeopleToolbarProps = {
  query: string;
  onQueryChange: (query: string) => void;
  searchMode: TeamsPeopleSearchMode;
  onSearchModeChange: (mode: TeamsPeopleSearchMode) => void;
  statuses: string[];
  onStatusesChange: (statuses: string[]) => void;
  joinedOn?: DateRange;
  onJoinedOnChange: (range: DateRange | undefined) => void;
  lastLogin?: DateRange;
  onLastLoginChange: (range: DateRange | undefined) => void;
  filters: TeamsPeopleFilters;
  onFiltersChange: (filters: TeamsPeopleFilters) => void;
  planOptions: TeamsPlanFilterOption[];
  isFiltered: boolean;
  onReset: () => void;
  columnVisibility: TeamsPeopleColumnVisibility;
  onColumnVisibilityChange: (next: TeamsPeopleColumnVisibility) => void;
};

// Hidden for now; set to true to show the Filter button (plan and credit access) again.
const SHOW_PEOPLE_FILTER = false;

/** People search and filters. Search, Status, Joined on and Last login work like the IAM table. */
export function TeamsPeopleToolbar({
  query,
  onQueryChange,
  searchMode,
  onSearchModeChange,
  statuses: selectedStatuses,
  onStatusesChange,
  joinedOn,
  onJoinedOnChange,
  lastLogin,
  onLastLoginChange,
  filters,
  onFiltersChange,
  planOptions,
  isFiltered,
  onReset,
  columnVisibility,
  onColumnVisibilityChange,
}: TeamsPeopleToolbarProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const mutedColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-500';
  const valueColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';
  // The shared filters bring their own height and radius; `!` keeps the Teams h-10 card shape
  // because cn does not group the custom `rounded-card` token with `rounded-[1rem]`.
  const triggerClassName = cn(
    teamsOutlineCardButtonClassName,
    '!h-10 !rounded-card shadow-none',
    valueColor,
    borderColor
  );

  const isEmailMode = searchMode === 'email';
  const SearchModeIcon = isEmailMode ? Mail : User;
  const searchField = isEmailMode ? t('EMAIL') : t('NAME');

  return (
    <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      {/* flex-1: without it the group is sized before the w-full search box grows, so the last filter wraps. */}
      <div className="flex min-w-0 flex-wrap items-center gap-2 lg:flex-1">
        <div
          className={cn(
            'flex h-10 w-full max-w-80 items-center gap-2.5 rounded-card border bg-true-white pl-3 pr-1 dark:bg-card',
            borderColor
          )}
        >
          <Search
            width={16}
            height={16}
            strokeWidth={1.5}
            className={cn('shrink-0', mutedColor)}
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={`${t('SEARCH_BY')} ${searchField.toLowerCase()}...`}
            aria-label={`${t('SEARCH_BY')} ${searchField.toLowerCase()}`}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-grey-500 md:text-sm',
              valueColor
            )}
          />
          {/* Same as the IAM table: the icon switches between searching by name and by email. */}
          <button
            type="button"
            onClick={() => onSearchModeChange(isEmailMode ? 'name' : 'email')}
            aria-label={isEmailMode ? t('SEARCH_BY_NAME', 'Search by name') : t('SEARCH_BY_EMAIL', 'Search by email')}
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-field hover:bg-grey-50 dark:hover:bg-muted',
              valueColor
            )}
          >
            <SearchModeIcon width={16} height={16} strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        <DataTableFacetedFilter
          title={t('STATUS')}
          options={statuses}
          value={selectedStatuses}
          onValueChange={onStatusesChange}
          triggerClassName={triggerClassName}
        />
        <DateRangeFilter
          title={t('JOINED_ON')}
          date={joinedOn}
          onDateChange={onJoinedOnChange}
          triggerClassName={triggerClassName}
          forceOutline
        />
        <DateRangeFilter
          title={t('LAST_LOGIN', 'Last login')}
          date={lastLogin}
          onDateChange={onLastLoginChange}
          triggerClassName={triggerClassName}
          forceOutline
        />
        {SHOW_PEOPLE_FILTER && (
          <TeamsPeopleFilter value={filters} onChange={onFiltersChange} planOptions={planOptions} />
        )}

        {isFiltered && (
          <button
            type="button"
            onClick={onReset}
            className={cn(
              'inline-flex h-10 shrink-0 items-center gap-2 rounded-card px-3 text-sm font-medium leading-body hover:bg-grey-50 dark:hover:bg-muted',
              valueColor
            )}
          >
            {t('RESET')}
            <Xmark width={16} height={16} strokeWidth={1.5} aria-hidden />
          </button>
        )}
      </div>

      <TeamsViewColumnsPopover
        visibility={columnVisibility}
        onVisibilityChange={onColumnVisibilityChange}
      />
    </div>
  );
}
